import { ApolloProvider } from "@apollo/client/react";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { MeDocument, type MeQuery } from "./generated/graphql";
import { apolloClient } from "./lib/graphql-client";
import { storage } from "./lib/storage";
import { store } from "./store";
import { setLoading, setUser, updateAccessToken } from "./store/slices/authSlice";
import "./styles/globals.css";

import { routeTree } from "./routeTree.gen";

const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

function AuthInitializer({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    const initAuth = async () => {
      const accessToken = storage.getAccessToken();
      const refreshToken = storage.getRefreshToken();

      if (accessToken && refreshToken) {
        store.dispatch(updateAccessToken(accessToken));

        try {
          const { data } = await apolloClient.query<MeQuery>({
            query: MeDocument,
            fetchPolicy: "network-only",
          });

          if (data?.me) {
            store.dispatch(setUser(data.me));
          }
        } catch (_error) {
          storage.clearTokens();
          store.dispatch(setLoading(false));
        }
      } else {
        store.dispatch(setLoading(false));
      }
    };

    initAuth();
  }, []);

  return <>{children}</>;
}

const root = document.getElementById("root");

if (!root) {
  throw new Error("Root element not found");
}

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <ErrorBoundary>
      <Provider store={store}>
        <ApolloProvider client={apolloClient}>
          <AuthInitializer>
            <RouterProvider router={router} />
          </AuthInitializer>
        </ApolloProvider>
      </Provider>
    </ErrorBoundary>
  </React.StrictMode>
);
